import { useEffect, useRef } from "react";
import { useForm } from "@tanstack/react-form";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Callout, Input } from "@inthememory/spectra-ui";

import { VusionLiveLogo } from "@/components/logos/VusionLiveLogo";
import { useOptionalSession } from "@/lib/session";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginFormRef = useRef<HTMLFormElement>(null);
  const session = useOptionalSession();

  const { auth_error: authError, reason: authReason, return_to: returnTo } = useSearch({ strict: false });

  const form = useForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      const loginHint = value.email.trim();
      const params = new URLSearchParams({ return_to: returnTo });
      if (loginHint) params.set("login_hint", loginHint);

      if (!loginFormRef.current) return;
      loginFormRef.current.action = `/api/v1/login?${params.toString()}`;
      loginFormRef.current.submit();
    },
  });

  useEffect(() => {
    if (session) {
      navigate({ to: returnTo, replace: true });
    }
  }, [session, navigate, returnTo]);

  return (
    <Card padding={10}>
      <div className="w-96 space-y-10">
        <div className="flex h-7 w-full items-center justify-center">
          <VusionLiveLogo />
        </div>

        <h4 className="text-center text-3xl text-slate-900">
          {t("auth.welcome")}
        </h4>

        {authError && (
          <Callout intent="error">
            <p>{t("auth.signInFailedDescription")}</p>
            <p className="mt-1 text-sm">
              {t("auth.errorCode", { code: authError })}
            </p>
            {authReason && (
              <p className="text-sm">
                {t("auth.errorReason", { reason: authReason })}
              </p>
            )}
          </Callout>
        )}

        <form
          ref={loginFormRef}
          className="space-y-3"
          method="POST"
          action={`/api/v1/login?${new URLSearchParams({ return_to: returnTo }).toString()}`}
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                const trimmed = value.trim();
                if (!trimmed) return t("auth.emailRequired");
                const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
                return isValidEmail ? undefined : t("auth.emailInvalid");
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Input
                  width="full"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                {field.state.meta.isTouched && field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-red-700">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button width="full" type="submit" disabled={!canSubmit || isSubmitting}>
                {t("auth.continue")}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </div>
    </Card>
  );
}
